import { Facebook, Instagram, Mail, MapPin, Music2 } from "lucide-react";
import { QUOTE_URL, SOCIALS, EMAIL, LOCATION, BUSINESS_NAME } from "@/lib/constants";
import { useSiteContent } from "@/lib/content";

export default function Footer() {
  const content = useSiteContent();
  const socials = content?.socials || SOCIALS;
  const text = content?.text || {};
  return (
    <footer id="contact" data-testid="site-footer" className="relative z-10 overflow-hidden bg-plum text-white/85">
      <img src="/brand/floral-tl.png" alt="" aria-hidden className="pointer-events-none absolute -left-8 -top-8 w-52 opacity-30" />
      <img src="/brand/floral-br.png" alt="" aria-hidden className="pointer-events-none absolute -bottom-10 -right-8 w-64 opacity-30" />

      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div>
            <p className="font-script text-3xl text-gold">Say hello</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              {text.footer_heading || "Let’s bake your moment"}
            </h2>
            <div className="mt-6 space-y-3 text-sm">
              <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-gold transition-colors">
                <Mail className="h-4 w-4 text-gold" /> {EMAIL}
              </a>
              <p className="flex items-center gap-3" data-testid="footer-location">
                <MapPin className="h-4 w-4 text-gold" /> {LOCATION} — Order via Facebook
              </p>
            </div>
            <div className="mt-6 flex items-center gap-4" data-testid="footer-socials">
              <a href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook-link"
                 className="rounded-full border border-white/20 p-2.5 transition-all hover:-translate-y-0.5 hover:border-gold hover:text-gold">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram-link"
                 className="rounded-full border border-white/20 p-2.5 transition-all hover:-translate-y-0.5 hover:border-gold hover:text-gold">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok-link"
                 className="rounded-full border border-white/20 p-2.5 transition-all hover:-translate-y-0.5 hover:border-gold hover:text-gold">
                <Music2 className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="rounded-3xl border border-gold/40 bg-white/5 p-8 text-center backdrop-blur">
            <p className="font-serif text-2xl italic text-white">Don’t see what you’re after?</p>
            <p className="mt-2 max-w-xs text-sm text-white/70">
              Send through your idea and I’ll help you with a custom quote.
            </p>
            <a
              href={QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-quote-button"
              className="mt-6 inline-block rounded-full border border-gold bg-gold px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-white shadow-[0_14px_36px_-10px_rgba(201,162,39,0.8)] transition-all duration-300 hover:-translate-y-1 hover:bg-golddeep"
            >
              Request a Quote
            </a>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-8 text-center">
          <p className="font-script text-2xl text-gold/90">{text.footer_note || "Thank you for supporting my small business!"}</p>
          <p className="mt-3 text-xs text-white/50">
            © {new Date().getFullYear()} {BUSINESS_NAME} ·{" "}
            <a href="/prices" className="underline decoration-gold/40 underline-offset-2 hover:text-gold" data-testid="footer-prices-link">
              Cupcake Prices
            </a>{" "}
            ·{" "}
            <a href="/servings-guide" className="underline decoration-gold/40 underline-offset-2 hover:text-gold" data-testid="footer-guide-link">
              Cake Sizes &amp; Servings Guide
            </a>{" "}
            ·{" "}
            <a href="/admin" className="underline decoration-gold/40 underline-offset-2 hover:text-gold" data-testid="footer-admin-link">
              Owner login
            </a>
          </p>
          <p className="mt-2 text-[10px] tracking-wide text-white/40" data-testid="footer-abn-line">
            {text.footer_legal ?? "Miss Cupcake Creations is owned and operated by Ashlee Clare Johnston — ABN 99 722 185 707"}
          </p>
        </div>
      </div>
    </footer>
  );
}
